import { useState, useMemo } from 'react'
import Link from 'next/link'
import useToastContext from "@byma/shared/hooks/useToastContext";
import { useRouter } from 'next/router'
import Layout from 'components/Layout'
import Loader from '@byma/shared/components/Loader'
import useAuth from '@byma/shared/hooks/useAuth'
import fetcher from '@byma/shared/helpers/fetcher'
import useSWR, { useSWRConfig } from 'swr'
import dayjs from 'dayjs'
import jsPDF from 'jspdf' 
import autoTable from 'jspdf-autotable'

import { receivableTypeParser, withdrawalStatusParser, anticipationStatusParser, methodParser, money } from '@byma/shared/helpers/formatters'

function OverviewReportTable({ overviewReport }) {
    return (
        <div className="overflow-auto box p-0 whitespace-nowrap w-full">
            <table className="text-center relative border-separate whitespace-nowrap md:w-full">
                <thead>
                    <tr>
                        <th className="p-3 sticky top-0 bg-white ">
                            Evento
                        </th>
                        <th className="p-3 sticky top-0 bg-white ">
                            Data
                        </th>
                        <th className="p-3 sticky top-0 bg-white ">
                            Valor em ingressos 
                        </th>
                        <th className="p-3 sticky top-0 bg-white ">
                            Receita ajustada
                        </th>
                        <th className="p-3 sticky top-0 bg-white ">
                            Receita não sacada
                        </th>
                        <th className="p-3 sticky top-0 bg-white ">
                            Saques
                        </th>
                    </tr>
                </thead>
                <tbody>
                    {overviewReport?.result?.sort((a,b) => new Date(b.partyDate) - new Date(a.partyDate)).map((item, index) => (
                        <tr className={`${index % 2 > 0 ? '' : 'bg-gray-100'} hover:bg-gray-200`}>
                            <td className="p-3">
                                <p className="text-left font-bold text-sm">{item.partyName}</p>
                            </td>
                            <td className="p-3">
                                <p className="text-sm">{dayjs(item.partyDate).format('DD/MM/YYYY HH:mm')}h</p>
                            </td>
                            <td className="p-3">
                                <p className="text-sm">{money(item.payments.order_amount_cents / 100)}</p>
                            </td>
                            <td className="p-3">
                                <p className="text-sm">{money((item.revenue - item.costs) / 100)}</p>
                            </td>
                            <td className="p-3">
                                <p className="text-sm">{money(item.revenueNotCollected / 100)}</p>
                            </td>
                            <td className="p-3">
                                <p className="text-sm">{money(item.withdrawal.amountCents / 100)}</p>
                            </td>
                        </tr>
                    ))}
                </tbody>
                <tfoot>
                    <tr>
                        <th className="p-3 sticky top-0 bg-white text-left">
                            Total
                        </th>
                        <th className="p-3 sticky top-0 bg-white">
                            -
                        </th>
                        <th className="p-3 sticky top-0 bg-white">
                            {money(overviewReport?.result?.reduce((acc, cur) => acc += cur.payments.order_amount_cents, 0) / 100)}
                        </th>
                        <th className="p-3 sticky top-0 bg-white">
                            {money((reportSum(overviewReport?.result, 'revenue') - reportSum(overviewReport?.result, 'costs')) / 100)}
                        </th>
                        <th className="p-3 sticky top-0 bg-white">
                            {money(reportSum(overviewReport?.result, 'revenueNotCollected') / 100)}
                        </th>
                        <th className="p-3 sticky top-0 bg-white">
                            {money(overviewReport?.result?.reduce((acc, cur) => acc += cur.withdrawal.amountCents, 0) / 100)}
                        </th>
                    </tr>
                </tfoot>
            </table>
        </div>
    )
}

function reportSum(report, key) {
    if (!report) return 0
    return report.reduce((acc, cur) => acc += cur[key], 0)
}

export default function Invoices() {
    const session = useAuth()
    const { query, ...router } = useRouter()
    const { token, user: currentUser } = session

    const { data: overviewReport, error: overviewReportError } = useSWR(token ? [`/api/getFutureEventsOverview?userId=${currentUser._id}`, null, token] : null, fetcher, { revalidateOnFocus: false })

    function downloadPDF() {
        const doc = new jsPDF();

        doc.setFontSize(8)


        doc.addImage('https://res.cloudinary.com/htkavmx5a/image/upload/c_scale,f_auto,h_120,q_auto:good/v1690549243/BYMA_LOGO_ESCRITO_ORANGE_kztj5z.png', 10, 10, 47, 12)

        doc.text('Relatório gerado em ' + dayjs().format('DD/MM/YYYY HH:mm:ss'), 10, 30)

        autoTable(doc, {
            headStyles: { 
                fillColor: '#ea560d',
                halign: 'center',
                valign: 'middle',
            },
            footStyles: {
                // darker color for the footer
                fillColor: '#f2eae0',
                textColor: '#000',
                valign: 'middle',
            },
            border: 0,
            margin: { left: 5, right: 5 },
            bodyStyles: {
                fontSize: 8,
            },
            startY: 50,
            head: [['Evento', 'Data', 'Valor em ingressos', 'Receita ajustada', 'Receita não sacada', 'Saques']],
            body: [
                ...(overviewReport?.result || []).sort((a, b) => new Date(b.partyDate) - new Date(a.partyDate)).map(item => [
                    item.partyName,
                    dayjs(item.partyDate).format('DD/MM/YYYY HH:mm') + 'h',
                    money(item.payments.order_amount_cents / 100),
                    money((item.revenue - item.costs) / 100),
                    money(item.revenueNotCollected / 100),
                    money(item.withdrawal.amountCents / 100)
                ])
            ],
            foot: [
                [
                    'Total',
                    '-',
                    money(overviewReport?.result?.reduce((acc, cur) => acc += cur.payments.order_amount_cents, 0) / 100),
                    money((reportSum(overviewReport?.result, 'revenue') - reportSum(overviewReport?.result, 'costs')) / 100),
                    money(reportSum(overviewReport?.result, 'revenueNotCollected') / 100),
                    money(overviewReport?.result?.reduce((acc, cur) => acc += cur.withdrawal.amountCents, 0) / 100)
                ]
            ],
        })

        let nextY = doc.lastAutoTable.finalY + 10

        doc.text('Valores e dados descritos aqui não são definitivos até o fechamento do evento e podem passar por alterações a qualquer momento', 10, nextY)

        doc.save(`events-overview-${(new Date()).valueOf()}.pdf`)
    }

    return (
        <Layout {...{session}}>
            <div className="flex space-x-3 p-3 shadow w-full overflow-x-auto">
                <Link href={`/invoices`}>
                    <a>
                        <button>
                            <div className="flex justify-between items-center cursor-pointer rounded shadow px-2 py-1 text-sm hover:shadow-md whitespace-nowrap">
                                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-5 h-5 mr-1">
                                    <path d="M12 9a1 1 0 01-1-1V3c0-.553.45-1.008.997-.93a7.004 7.004 0 015.933 5.933c.078.547-.378.997-.93.997h-5z" />
                                    <path d="M8.003 4.07C8.55 3.992 9 4.447 9 5v5a1 1 0 001 1h5c.552 0 1.008.45.93.997A7.001 7.001 0 012 11a7.002 7.002 0 016.003-6.93z" />
                                </svg>

                                <p>
                                    Relatório de eventos
                                </p>
                            </div>
                        </button>
                    </a>
                </Link>
            </div>
            <div className="p-3">
                {overviewReportError ? (
                    <div className="p-3 bg-red-100 text-red-700 rounded mb-3">
                        <p className="font-bold">
                            {overviewReportError?.info?.message === 'Forbidden' ? 
                                    'Você não tem permissão para acessar este relatório'
                                    : 'Erro ao carregar dados'}
                        </p>
                    </div>
                ) : (
                    <>
                        <div className="p-3 rounded box w-full text-gray-700 mb-6 flex space-x-3 items-center">
                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-5 h-5">
                                <path fillRule="evenodd" d="M18 10a8 8 0 1 1-16 0 8 8 0 0 1 16 0Zm-7-4a1 1 0 1 1-2 0 1 1 0 0 1 2 0ZM9 9a.75.75 0 0 0 0 1.5h.253a.25.25 0 0 1 .244.304l-.459 2.066A1.75 1.75 0 0 0 10.747 15H11a.75.75 0 0 0 0-1.5h-.253a.25.25 0 0 1-.244-.304l.459-2.066A1.75 1.75 0 0 0 9.253 9H9Z" clipRule="evenodd" />
                            </svg>

                            <div>
                                <p className="tracking-tight font-bold">Atenção!</p>
                                <p>
                                    Valores e dados descritos aqui não são definitivos até o fechamento do evento e podem passar por alterações a qualquer momento
                                </p>
                            </div>
                        </div>
                        <div className="box">
                            <div className="flex justify-between items-center mb-3">
                                <p className="text-md font-bold">Visão geral</p>
                                <button className="flex justify-between items-center cursor-pointer rounded shadow px-2 py-1 text-sm hover:shadow-md whitespace-nowrap focus:shadow-sm" onClick={downloadPDF}>
                                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-5 h-5 mr-1">
                                        <path d="M10.75 2.75a.75.75 0 00-1.5 0v8.614L6.295 8.235a.75.75 0 10-1.09 1.03l4.25 4.5a.75.75 0 001.09 0l4.25-4.5a.75.75 0 00-1.09-1.03l-2.955 3.129V2.75z" />
                                        <path d="M3.5 12.75a.75.75 0 00-1.5 0v2.5A2.75 2.75 0 004.75 18h10.5A2.75 2.75 0 0018 15.25v-2.5a.75.75 0 00-1.5 0v2.5c0 .69-.56 1.25-1.25 1.25H4.75c-.69 0-1.25-.56-1.25-1.25v-2.5z" />
                                    </svg>

                                    <p>PDF</p>
                                </button>
                            </div>
                            <OverviewReportTable overviewReport={overviewReport} />
                        </div>
                    </>
                )}


            </div>
        </Layout>
    )
}

